import { ChangeEvent, KeyboardEvent, forwardRef, useState, useRef, useEffect } from 'react'
import styled from 'styled-components'

import TextInput, { TextInputProps } from '../TextInput'
import Box from '../Box'

import { KeyCode } from 'utils/constants/keyCode'

import { Variant } from './Chip'
import TextButton from 'components/Buttons/TextButton'

export interface NewChipProps {
  variant?: Variant
  value?: string
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void
  onBlur?: () => void
  onPressEnter?: () => void
  placeholder?: string
  error?: string
  isInputHidden?: boolean
  isAddButtonHidden?: boolean
}

const NewChip = forwardRef<HTMLInputElement, NewChipProps & Omit<TextInputProps, 'variant' | 'onChange'>>(
  (
    {
      variant = 'pill',
      value,
      onChange,
      onBlur,
      onPressEnter,
      placeholder,
      error,
      isInputHidden = false,
      isAddButtonHidden = false,
      ...props
    },
    _ref,
  ) => {
    const [isFocused, setIsFocused] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)

    const handlePressEnter = (e: KeyboardEvent<HTMLInputElement>) => {
      e.stopPropagation()

      if (e.key === KeyCode.enter) {
        onPressEnter?.()
      }
    }

    const handleFocus = () => {
      setIsFocused(true)
      inputRef?.current?.click()
      inputRef?.current?.focus()
    }

    useEffect(() => {
      if (isFocused) {
        inputRef?.current?.focus()
      }
    }, [isFocused])

    return (
      <Container variant={variant}>
        {!isAddButtonHidden && (
          <Box display={isFocused ? 'none' : 'block'}>
            <TextButton icon={['fas', 'plus']} onClick={handleFocus}>
              Add
            </TextButton>
          </Box>
        )}
        {!isInputHidden && (
          <Box display={isFocused ? 'block' : 'none'}>
            <TextInput
              ref={inputRef}
              width="auto"
              variant={variant === 'badge' ? 'boxless' : undefined}
              value={value}
              onChange={onChange}
              onBlur={() => {
                onBlur?.()
                setIsFocused(false)
              }}
              onKeyPress={handlePressEnter}
              placeholder={placeholder}
              error={error}
              height={props?.height}
              marginTop={error && props?.marginTop}
            />
          </Box>
        )}
      </Container>
    )
  },
)

const Container = styled.span<{ variant: Variant }>`
  ${(p) =>
    p.variant === 'pill'
      ? `input {
          padding: 6px 20px;
          width: 200px;
          border-radius: 16px;
          border-style: dashed;
        }`
      : ``}
  margin: auto 0 auto 0;
`

export default NewChip
